import React from "react";
//#region types
import { AdvancedEditableTableProps, WintFormKey } from "models/propTypes/wintFormPropsTypes";
//#endregion
//#region components
import {
  TableContainer,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Grid,
  TableFooter as MuiTableFooter,
} from "@mui/material";
import AdvancedEditableTableRow from "./advancedEditableTableRow";
import { TableFooter } from "components/common/tables";
//#endregion
import { getColumnHeader, getFooterLayout } from "./advancedEditableTableLayout";
import { UtilsHelper } from "utils/helpers";
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";

function AdvancedEditableTable<T extends Record<string, any>>(props: AdvancedEditableTableProps<T>) {
  const {
    connect,
    name,
    value,
    fields,
    isCompactTable,
    extraCompact,
    defaultRow,
    showRowIndexes,
    hideActions,
    allowPagination,
    hideActionsCell,
    showColumnHeaders,
    defaultRowsPerPage,
    style,
    footerLabel,
  } = props;

  const maxRowsPerPage = 30;
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPage] = React.useState<number>(defaultRowsPerPage ?? maxRowsPerPage);
  const pageStartIndex = page * rowsPerPage;
  const pageEndIndex = pageStartIndex + rowsPerPage;

  const onPageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const changeRowsPerPageHandler = (rowsPerPage: number) => {
    setPage(0);
    setRowsPage(rowsPerPage);
  };

  const updateRow = React.useCallback((index: number, newRow: T, shouldValidate?: boolean) => {
    connect(`${name}[${index}]` as WintFormKey<T>).setValue(newRow, shouldValidate);
  }, []);

  const removeRow = React.useCallback(
    (index: number) => {
      const connection = connect(name);
      const rows = [...(connection.value ?? [])];
      rows.splice(index, 1);
      connection.setValue(rows, true);
    },
    [connect]
  );

  const newRow = React.useMemo(
    () =>
      hideActions
        ? null
        : ({
            ...defaultRow,
            [props.idField!]: UtilsHelper.getUniqueId(),
          } as T),
    [defaultRow, props.idField, value.length, hideActions]
  );

  const rows = React.useMemo(() => (newRow ? [...(value ?? []), newRow] : [...(value ?? [])]), [value, newRow]);

  const hasFixedWidthColumns = Object.values(fields).some((field) => field?.cellWidth != null);

  const getColumnHeaders = () => {
    if (!showColumnHeaders) return null;

    return (
      <TableHead>
        <TableRow className={cssExports.columnHeaderRow}>
          {showRowIndexes && <TableCell></TableCell>}
          {Object.keys(fields).map((key) => {
            const field = fields[key as keyof T];
            const widthValue =
              field?.cellWidth ??
              (field?.widthColumnCount && typeof field.widthColumnCount === "number"
                ? `${(field.widthColumnCount / 12) * 100}%`
                : undefined);

            return (
              <TableCell
                key={`header-${key}`}
                align={field?.alignment}
                className={clsx(cssExports.tableCell, cssExports.blackHighlightText, field?.className)}
                style={{ width: widthValue, maxWidth: field?.cellWidth }}
              >
                {getColumnHeader(field)}
              </TableCell>
            );
          })}
          {!hideActionsCell && <TableCell className={cssExports.editableTableActionButton}></TableCell>}
        </TableRow>
      </TableHead>
    );
  };

  const getFooter = () => {
    const layout = getFooterLayout(fields, showRowIndexes);
    if (!layout) return null;

    return (
      <MuiTableFooter>
        <TableRow className={cssExports.editableTableFooterRow}>
          {layout.labelColSpan > 0 && (
            <TableCell colSpan={layout.labelColSpan} align="right">
              {footerLabel}
            </TableCell>
          )}
          {layout.footerKeys.map((key) => {
            const field = fields[key]!;
            return (
              <TableCell key={`footer-${String(key)}`} align={field.alignment} className={field.className}>
                {field.footer}
              </TableCell>
            );
          })}
          {!hideActionsCell && <TableCell className={cssExports.editableTableActionButton}></TableCell>}
        </TableRow>
      </MuiTableFooter>
    );
  };

  return (
    <Grid
      container
      className={clsx(
        cssExports.tableListContainer,
        cssExports.editableTableContainer,
        isCompactTable ? cssExports.compactTable : "",
        isCompactTable && extraCompact ? cssExports.extraCompactTable : "",
        props.className
      )}
      direction="column"
      style={style}
      data-testid={`${props.name}AdvancedEditableTable`}
    >
      <TableContainer className={cssExports.tableContainerOuter} classes={{ root: cssExports.tableContainerRoot }}>
        <Table aria-label="simple table" style={hasFixedWidthColumns ? { tableLayout: "fixed" } : undefined}>
          {getColumnHeaders()}
          <TableBody className={cssExports.tableBody}>
            {(allowPagination ? rows.slice(pageStartIndex, pageEndIndex) : rows).map((row, rowIndex) => (
              <AdvancedEditableTableRow<T>
                key={`table-row-${row[props.idField!] ?? rowIndex}`}
                connect={connect}
                name={name}
                index={rowIndex + pageStartIndex}
                row={row}
                fields={fields}
                idField={props.idField}
                updateRow={updateRow}
                removeRow={removeRow}
                isLastRow={rowIndex + pageStartIndex === rows.length - 1 && !!newRow}
                showRowIndex={showRowIndexes}
                hideActions={hideActions}
                hideActionsCell={hideActionsCell}
                data-testid={`${props.name}AdvancedEditableTableRow${rowIndex}`}
              />
            ))}
          </TableBody>
          {getFooter()}
        </Table>
        {allowPagination ? (
          <Grid container className={cssExports.tablePaginationContainer}>
            <TableFooter
              total={rows.length}
              rowsPerPage={rowsPerPage}
              maxRowsPerPage={maxRowsPerPage}
              currentPage={page}
              advancedPagination
              hideIfSinglePage
              hideItemRange={props.hideItemRange}
              hideRowsPerPageSelector={props.hideRowsPerPageSelector}
              changePageHandler={onPageChange}
              changeRowsPerPageHandler={changeRowsPerPageHandler}
            />
          </Grid>
        ) : (
          <></>
        )}
      </TableContainer>
    </Grid>
  );
}

export default AdvancedEditableTable;
